import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { waLink } from "@/lib/contact";
import Logo from "@/components/Logo";
import { AnimatePresence, motion } from "framer-motion";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const NAV = [
    { label: t("nav_home"), to: "/", key: "home" },
    { label: t("nav_catalog"), to: "/catalogo", key: "catalog" },
    { label: t("nav_athletes"), to: "/atletas", key: "athletes" },
    { label: t("nav_about"), to: "/sobre-vorna", key: "about" },
    { label: t("nav_contact"), to: "/contacto", key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/catalogo?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(""); }
  };

  const LangSwitch = ({ className = "" }) => (
    <div className={`flex items-center border border-white/15 text-[10px] font-bold ${className}`}>
      {["es", "en"].map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
          className={`px-2.5 py-1 tracking-widest transition-colors ${lang === l ? "bg-vorna-accent text-white" : "text-white/60 hover:text-white"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <header data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-white/10" : "bg-gradient-to-b from-black/70 to-transparent border-transparent"
      }`}>
      <div className="mx-auto max-w-[1600px] px-5 lg:px-10">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <div className="flex items-center gap-3 lg:hidden">
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)} aria-label="Menu"><Menu className="h-6 w-6" /></button>
          </div>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.slice(0, 3).map((n) => (
              <Link key={n.key} to={n.to} data-testid={`nav-${n.key}`}
                className="text-xs font-semibold tracking-widest text-white/80 hover:text-vorna-accent transition-colors">{n.label}</Link>
            ))}
          </nav>

          <div className="absolute left-1/2 -translate-x-1/2"><Logo imgClass="h-9 lg:h-10" /></div>

          <div className="flex items-center gap-4 xl:gap-6">
            <nav className="hidden lg:flex items-center gap-6">
              {NAV.slice(3).map((n) => (
                <Link key={n.key} to={n.to} data-testid={`nav-${n.key}`}
                  className="text-xs font-semibold tracking-widest text-white/80 hover:text-vorna-accent transition-colors">{n.label}</Link>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <LangSwitch className="hidden sm:flex" />
              <button data-testid="search-toggle" onClick={() => setSearchOpen((s) => !s)} aria-label="Search">
                <Search className="h-5 w-5 hover:text-vorna-accent transition-colors" />
              </button>
              <a data-testid="nav-whatsapp" href={waLink("")} target="_blank" rel="noreferrer"
                className="hidden sm:inline-flex h-9 w-9 items-center justify-center bg-vorna-accent hover:bg-vorna-accentHover transition-colors" aria-label="WhatsApp">
                <MessageCircle className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {searchOpen && (
            <motion.form onSubmit={submitSearch} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <input autoFocus data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={lang === "es" ? "BUSCAR EN VORNA..." : "SEARCH VORNA..."}
                className="w-full bg-transparent border-b border-white/20 py-4 text-lg font-display uppercase tracking-wide placeholder:text-white/30 focus:outline-none focus:border-vorna-accent" />
            </motion.form>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black lg:hidden" data-testid="mobile-menu">
            <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
              <Logo imgClass="h-8" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)}><X className="h-6 w-6" /></button>
            </div>
            <nav className="flex flex-col p-6 gap-1">
              {NAV.map((n, i) => (
                <motion.div key={n.key} initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}>
                  <Link to={n.to} onClick={() => setOpen(false)} className="block py-4 font-display text-3xl uppercase tracking-tight border-b border-white/5 hover:text-vorna-accent transition-colors">{n.label}</Link>
                </motion.div>
              ))}
              <div className="mt-6 flex items-center gap-3">
                <LangSwitch />
                <a href={waLink("")} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-2 bg-vorna-accent px-4 py-2 text-sm font-bold"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
